<script setup lang="ts">
import { inject, provide, ref } from "vue";

import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import MobileFilterArea from "~/components/filters/MobileFilterArea.vue";
import SortDropdown from "~/components/filters/SortDropdown.vue";
import GroupByDropdown from "~/components/GroupByDropdown.vue";
import MobileBottomSheet from "~/components/MobileBottomSheet.vue";
import PropertiesDropdown from "~/components/PropertiesDropdown.vue";
import { PARENT_ON_AFTER_CLOSE_FUNCTION, PARENT_ON_AFTER_OPEN_FUNCTION } from "~/constants/injection";
import { LAYOUT_KIND_TO_NAME_AND_ICON } from "~/constants/layout";
import { FilterIcon } from "~/icons";
import { LayoutKind } from "~/shared/enums";
import { useAppStore, usePageStore } from "~/stores";

const appStore = useAppStore();
const pageStore = usePageStore();

const parentOnAfterOpen = inject(PARENT_ON_AFTER_OPEN_FUNCTION, null);
const parentOnAfterClose = inject(PARENT_ON_AFTER_CLOSE_FUNCTION, null);

const isOpen = ref(false);
const isHidden = ref(false);
const selectedFilterDuid = ref<string | null>(null);

const filterArea = ref<InstanceType<typeof MobileFilterArea> | null>(null);

const openFilterArea = () => {
  if (!filterArea.value) {
    return;
  }
  filterArea.value.show = true;
  filterArea.value.showAllFilters = true;
};

const open = () => {
  isOpen.value = true;
  if (parentOnAfterOpen) {
    parentOnAfterOpen();
  }
};

const close = () => {
  isOpen.value = false;
  if (parentOnAfterClose) {
    parentOnAfterClose();
  }
};

const setLayoutKind = (layoutKind: LayoutKind) => {
  appStore.setLayoutKind(layoutKind);
};

provide(PARENT_ON_AFTER_OPEN_FUNCTION, () => {
  isHidden.value = true;
});
provide(PARENT_ON_AFTER_CLOSE_FUNCTION, () => {
  isHidden.value = false;
});

defineExpose({
  open,
  close,
});
</script>

<template>
  <MobileBottomSheet
    v-if="pageStore.isMobile"
    :entity="isOpen"
    :hidden="isHidden"
    title="Manage layout"
    include-done
    @close="close">
    <div class="mb-1 mt-3 flex flex-col gap-3">
      <div class="mx-4 grid grid-cols-2 overflow-hidden rounded-md border text-md border-hvy">
        <div
          v-for="([kind, [title, icon]], index) in LAYOUT_KIND_TO_NAME_AND_ICON"
          :key="kind"
          class="flex items-center justify-center gap-2 px-3 py-4 border-md"
          :class="[kind === appStore.layoutKind && 'bg-md', index % 2 === 1 && 'border-l', index >= 2 && 'border-t']"
         
          @click="setLayoutKind(kind)"
          @keydown.enter="setLayoutKind(kind)">
          <component :is="icon" class="pt-0.5 icon-md" />
          <span class="select-none text-base/4">{{ title }}</span>
        </div>
      </div>
      <div class="flex flex-col px-1.5">
        <div class="py-2">
          <DropdownMenuItemContent
            :icon="FilterIcon"
            title="Filters"
            class="text-md"
            is-submenu
            :right-text="appStore.filters.length"
            @click="openFilterArea" />
        </div>
        <MobileFilterArea ref="filterArea" :selected-filter-duid="selectedFilterDuid" />
        <SortDropdown />
        <GroupByDropdown
          :layout-kind="appStore.layoutKind"
          :group-by="appStore.groupBy"
          @set-group-by="appStore.setGroupBy" />
        <PropertiesDropdown shown-properties />
        <PropertiesDropdown default-properties />
      </div>
    </div>
  </MobileBottomSheet>
</template>
